// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Search, Shield, TrendingUp, CheckCircle, Clock, RefreshCw, Eye, Filter, Download, Zap, BarChart3, Target, Activity } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const VerificareDiscrepante = () => {
  const [autoScanEnabled, setAutoScanEnabled] = useState(true);
  const [lastScanTime, setLastScanTime] = useState(new Date());
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');

  // Auto-refresh scan time every 30 seconds
  useEffect(() => {
    if (!autoScanEnabled) return;
    const interval = setInterval(() => {
      setLastScanTime(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [autoScanEnabled]);

  // Mock data pentru verificare discrepanțe
  const discrepancyData = {
    overview: {
      totalChecks: 1247,
      activeDiscrepancies: 23,
      criticalIssues: 3,
      resolvedToday: 15,
      accuracyRate: 97.8,
      lastScan: lastScanTime.toLocaleTimeString('ro-RO')
    },
    discrepancies: [
      {
        id: 'DISC-2024-001',
        type: 'Diferență Balanta',
        category: 'Financial Balance',
        severity: 'Critical',
        status: 'Active',
        description: 'Diferența de 2,450 RON între balanta contabilă și registrul de casă',
        detectedAt: '2024-03-15 14:32:15',
        affectedAccounts: ['5311 - Casa în RON', '4111 - Furnizori'],
        amount: 2450.00,
        currency: 'RON',
        confidence: 98.5,
        autoResolution: false,
        impact: 'High',
        recommendations: [
          'Verifică toate tranzacțiile din data de 14.03.2024',
          'Reconciliază extractele bancare',
          'Analizează înregistrările duplicat'
        ]
      },
      {
        id: 'DISC-2024-002',
        type: 'TVA Incorect Calculat',
        category: 'Tax Calculation',
        severity: 'High',
        status: 'In Progress',
        description: 'TVA calculat incorect pe factura FV-2024-0156 - diferență 95.20 RON',
        detectedAt: '2024-03-15 13:45:22',
        affectedAccounts: ['4427 - TVA de plată'],
        amount: 95.20,
        currency: 'RON',
        confidence: 95.2,
        autoResolution: true,
        impact: 'Medium',
        recommendations: [
          'Recalculează TVA-ul cu cota corectă',
          'Verifică configurarea cotelor TVA în sistem',
          'Actualizează nota de storno'
        ]
      },
      {
        id: 'DISC-2024-003',
        type: 'Dublă Înregistrare',
        category: 'Duplicate Entry',
        severity: 'Medium',
        status: 'Resolved',
        description: 'Aceeași factură de achiziție înregistrată de două ori - AI-2024-0089',
        detectedAt: '2024-03-15 11:20:10',
        affectedAccounts: ['6021 - Cheltuieli materiale', '4011 - Furnizori'],
        amount: 1234.56,
        currency: 'RON',
        confidence: 99.8,
        autoResolution: true,
        impact: 'Medium',
        recommendations: [
          'Anulează înregistrarea duplicată',
          'Verifică procesul de aprobare facturile',
          'Îmbunătățește validarea automatizată'
        ]
      },
      {
        id: 'DISC-2024-004',
        type: 'Sold Negativ Client',
        category: 'Customer Balance',
        severity: 'Low',
        status: 'Active',
        description: 'Client SC COMPANY SRL are sold negativ -567.89 RON fără justificare',
        detectedAt: '2024-03-15 10:15:45',
        affectedAccounts: ['4111 - Clienți'],
        amount: -567.89,
        currency: 'RON',
        confidence: 89.3,
        autoResolution: false,
        impact: 'Low',
        recommendations: [
          'Verifică toate facturile clientului',
          'Analizează plățile în avans',
          'Contactează clientul pentru clarificări'
        ]
      }
    ],
    checkRules: [
      {
        id: 'RULE-001',
        name: 'Verificare Balanta Contabilă',
        category: 'Balance Check',
        frequency: 'Real-time',
        status: 'Active',
        description: 'Verifică că totalul debitor = totalul creditor în balanta de verificare',
        lastRun: '2024-03-15 15:00:00',
        executionTime: '2.3s',
        checksPerformed: 1247,
        issuesFound: 3,
        successRate: 99.8
      },
      {
        id: 'RULE-002',
        name: 'Validare Calcul TVA',
        category: 'Tax Validation',
        frequency: 'Per Transaction',
        status: 'Active',
        description: 'Verifică calculul corect al TVA-ului pentru toate tranzacțiile',
        lastRun: '2024-03-15 14:58:30',
        executionTime: '0.8s',
        checksPerformed: 4567,
        issuesFound: 12,
        successRate: 99.7
      },
      {
        id: 'RULE-003',
        name: 'Detectare Duplicări',
        category: 'Duplicate Detection',
        frequency: 'Hourly',
        status: 'Active',
        description: 'Identifică facturi, plăți sau înregistrări duplicate în sistem',
        lastRun: '2024-03-15 15:00:00',
        executionTime: '15.6s',
        checksPerformed: 2389,
        issuesFound: 8,
        successRate: 99.7
      },
      {
        id: 'RULE-004',
        name: 'Reconciliere Bancară',
        category: 'Bank Reconciliation',
        frequency: 'Daily',
        status: 'Active',
        description: 'Compară soldurile contabile cu extractele bancare',
        lastRun: '2024-03-15 09:00:00',
        executionTime: '45.2s',
        checksPerformed: 156,
        issuesFound: 2,
        successRate: 98.7
      }
    ],
    trends: {
      daily: [
        { day: 'Lun', discrepancies: 12, resolved: 10 },
        { day: 'Mar', discrepancies: 18, resolved: 15 },
        { day: 'Mie', discrepancies: 23, resolved: 20 },
        { day: 'Joi', discrepancies: 15, resolved: 12 },
        { day: 'Vin', discrepancies: 23, resolved: 15 }
      ],
      categories: [
        { category: 'Financial Balance', count: 8, percentage: 35 },
        { category: 'Tax Calculation', count: 6, percentage: 26 },
        { category: 'Duplicate Entry', count: 5, percentage: 22 },
        { category: 'Customer Balance', count: 4, percentage: 17 }
      ]
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'error';
      case 'In Progress': return 'warning';
      case 'Resolved': return 'success';
      case 'Ignored': return 'default';
      default: return 'default';
    }
  };

  const filteredDiscrepancies = discrepancyData.discrepancies.filter(item => {
    const matchesSearch = item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || item.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  const discrepancyColumns = [
    { 
      header: 'ID & Tip', 
      key: 'id',
      render: (value, row) => (
        <div>
          <div className="font-mono text-sm font-medium">{value}</div>
          <div className="text-xs text-gray-500">{row.type}</div>
        </div>
      )
    },
    { 
      header: 'Descriere', 
      key: 'description',
      render: (value) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {value}
          </p>
        </div>
      )
    },
    { 
      header: 'Severitate', 
      key: 'severity',
      render: (value) => <Badge variant={getSeverityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Sumă', 
      key: 'amount',
      render: (value, row) => (
        <div className="text-right">
          <div className={`font-semibold ${
            Math.abs(value) > 1000 ? 'text-red-600' : 'text-yellow-600'
          }`}>
            {value.toLocaleString('ro-RO', { 
              style: 'currency', 
              currency: row.currency 
            })}
          </div>
        </div>
      )
    },
    { 
      header: 'AI Confidence', 
      key: 'confidence',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 95 ? 'bg-green-500' : value > 85 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Auto-Fix', 
      key: 'autoResolution',
      render: (value) => (
        <div className="flex items-center justify-center">
          {value ? (
            <Zap className="w-4 h-4 text-green-500" title="Auto-rezolvare disponibilă" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-yellow-500" title="Necesită intervenție manuală" />
          )}
        </div>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedDiscrepancy(row);
              setShowDiscrepancyModal(true);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm"
            title="Vezi Detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const ruleColumns = [
    { header: 'Regula', key: 'name' },
    { header: 'Categorie', key: 'category' },
    { header: 'Frecvență', key: 'frequency' },
    { header: 'Status', key: 'status', render: (value) => <Badge variant="success">{value}</Badge> },
    { header: 'Success Rate', key: 'successRate', render: (value) => `${value}%` },
    { header: 'Checks', key: 'checksPerformed', render: (value) => value.toLocaleString() },
    { header: 'Issues', key: 'issuesFound' },
    { header: 'Ultima Rulare', key: 'lastRun' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Verificare Discrepanțe în Timp Real</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Monitorizare continuă și alertare automată pentru inconsistențele contabile
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <RefreshCw className={`w-4 h-4 mr-2 ${autoScanEnabled ? 'animate-spin text-green-500' : 'text-gray-400'}`} />
            Ultima verificare: {discrepancyData.overview.lastScan}
            <span className="mx-2">•</span>
            <Shield className="w-4 h-4 mr-1" />
            <span className={`${discrepancyData.overview.accuracyRate > 95 ? 'text-green-600' : 'text-yellow-600'}`}>
              Acuratețe: {discrepancyData.overview.accuracyRate}%
            </span>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            className={`btn-secondary ${autoScanEnabled ? 'bg-green-100 dark:bg-green-800' : 'bg-gray-100'}`}
            onClick={() => setAutoScanEnabled(!autoScanEnabled)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoScanEnabled ? 'animate-spin' : ''}`} />
            {autoScanEnabled ? 'Auto-Scan ON' : 'Auto-Scan OFF'}
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Raport
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowRuleModal(true)}
          >
            <Shield className="w-4 h-4 mr-2" />
            Configurare Reguli
          </button>
        </div>
      </div>

      {/* Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <Card title="Total Verificări" subtitle="Astăzi" icon={Search}>
          <div className="text-2xl font-bold text-primary-600">{discrepancyData.overview.totalChecks}</div>
          <div className="text-sm text-blue-600">Automat & manual</div>
        </Card>
        <Card title="Discrepanțe Active" subtitle="Necesită atenție" icon={AlertTriangle}>
          <div className="text-2xl font-bold text-red-600">{discrepancyData.overview.activeDiscrepancies}</div>
          <div className="text-sm text-red-600">Real-time detection</div>
        </Card>
        <Card title="Issues Critice" subtitle="Prioritate înaltă" icon={Target}>
          <div className="text-2xl font-bold text-red-600">{discrepancyData.overview.criticalIssues}</div>
          <div className="text-sm text-red-600">Acțiune imediată</div>
        </Card>
        <Card title="Rezolvate Azi" subtitle="Progres zilnic" icon={CheckCircle}>
          <div className="text-2xl font-bold text-green-600">{discrepancyData.overview.resolvedToday}</div>
          <div className="text-sm text-green-600">+25% vs ieri</div>
        </Card>
        <Card title="Rata Acuratețe" subtitle="Sistem general" icon={TrendingUp}>
          <div className="text-2xl font-bold text-green-600">{discrepancyData.overview.accuracyRate}%</div>
          <div className="text-sm text-green-600">Target: 95%+</div>
        </Card>
        <Card title="Status Sistem" subtitle="Monitorizare" icon={Activity}>
          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${autoScanEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <div className="text-lg font-bold text-green-600">
              {autoScanEnabled ? 'Active' : 'Paused'}
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Discrepancies Table */}
        <div className="lg:col-span-3">
          <Card title="Discrepanțe Detectate" subtitle="Inconsistențe identificate automat de sistem">
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută discrepanță sau descriere..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select 
                value={severityFilter} 
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Toate severitățile</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <button className="btn-secondary">
                <Filter className="w-4 h-4 mr-2" />
                Filtre Avansate
              </button>
            </div>
            <Table columns={discrepancyColumns} data={filteredDiscrepancies} />
          </Card>
        </div>

        {/* Sidebar Analytics */}
        <div className="space-y-6">
          <Card title="Trendul Săptămânal" icon={BarChart3}>
            <div className="space-y-3">
              {discrepancyData.trends.daily.map((day, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <span className="text-sm font-medium">{day.day}</span>
                  <div className="flex items-center space-x-3">
                    <div className="text-xs">
                      <span className="text-red-600">{day.discrepancies}</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-green-600">{day.resolved}</span>
                    </div>
                    <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full" 
                        style={{ width: `${(day.resolved / day.discrepancies) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Categorii Discrepanțe" icon={Target}>
            <div className="space-y-3">
              {discrepancyData.trends.categories.map((cat, index) => (
                <div key={index} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {cat.category}
                    </span>
                    <span className="text-sm font-semibold text-primary-600">
                      {cat.count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full" 
                      style={{ width: `${cat.percentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{cat.percentage}% din total</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Acțiuni Rapide">
            <div className="space-y-3">
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Scan Manual Complet</span>
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Auto-Fix Available</span>
                  <Badge variant="success" className="text-xs">8</Badge>
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Export Daily Report</span>
                  <Download className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Check Rules Table */}
      <Card title="Reguli de Verificare Active" subtitle="Algoritmi de monitorizare automată a integrității datelor">
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Sistemul rulează {discrepancyData.checkRules.length} reguli automate pentru detectarea inconsistențelor în timp real.
        </div>
        <Table columns={ruleColumns} data={discrepancyData.checkRules} />
      </Card>

      {/* Discrepancy Detail Modal */}
      <Modal
        isOpen={showDiscrepancyModal}
        onClose={() => {
          setShowDiscrepancyModal(false);
          setSelectedDiscrepancy(null);
        }}
        title={`Discrepanță: ${selectedDiscrepancy?.id}`}
        size="large"
      >
        {selectedDiscrepancy && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span className="font-mono">{selectedDiscrepancy.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Tip:</span>
                    <span>{selectedDiscrepancy.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Categorie:</span>
                    <span>{selectedDiscrepancy.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Severitate:</span>
                    <Badge variant={getSeverityColor(selectedDiscrepancy.severity)}>
                      {selectedDiscrepancy.severity}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <Badge variant={getStatusColor(selectedDiscrepancy.status)}>
                      {selectedDiscrepancy.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Impact Financiar</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                      Sumă Afectată
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {selectedDiscrepancy.amount.toLocaleString('ro-RO', { 
                        style: 'currency', 
                        currency: selectedDiscrepancy.currency 
                      })}
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                      AI Confidence
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${selectedDiscrepancy.confidence}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold">{selectedDiscrepancy.confidence}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere Problemă</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {selectedDiscrepancy.description}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Conturi Afectate</h3>
                <div className="space-y-2">
                  {selectedDiscrepancy.affectedAccounts.map((account, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-400">{account}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Recomandări AI</h3>
                <div className="space-y-2">
                  {selectedDiscrepancy.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start space-x-2 text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-400">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                {selectedDiscrepancy.autoResolution ? (
                  <Zap className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                )}
                <span className="font-medium text-gray-900 dark:text-white">
                  {selectedDiscrepancy.autoResolution ? 'Auto-rezolvare Disponibilă' : 'Necesită Intervenție Manuală'}
                </span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedDiscrepancy.autoResolution 
                  ? 'Sistemul poate rezolva automat această discrepanță folosind regulile predefinite.'
                  : 'Această discrepanță necesită analiză și rezolvare manuală din partea contabilului.'
                }
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Mark as Ignored</button>
              {selectedDiscrepancy.autoResolution && (
                <button className="btn-secondary">
                  <Zap className="w-4 h-4 mr-2" />
                  Auto-Fix Now
                </button>
              )}
              <button className="btn-primary">Mark as Resolved</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Rules Configuration Modal */}
      <Modal
        isOpen={showRuleModal}
        onClose={() => setShowRuleModal(false)}
        title="Configurare Reguli de Verificare"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Setări Generale</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-scan în timp real</span>
                  <p className="text-xs text-gray-500">Verifică discrepanțele la fiecare tranzacție</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={autoScanEnabled}
                  onChange={(e) => setAutoScanEnabled(e.target.checked)}
                  className="rounded" 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-rezolvare probleme simple</span>
                  <p className="text-xs text-gray-500">Rezolvă automat discrepanțele cu confidence > 95%</p>
                </div>
                <input type="checkbox" defaultChecked className="rounded" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Alertă email pentru issues critice</span>
                  <p className="text-xs text-gray-500">Trimite notificări pentru discrepanțele Critical și High</p>
                </div>
                <input type="checkbox" defaultChecked className="rounded" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Threshold-uri Alertare</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sumă minimă pentru alertă Critical (RON)
                </label>
                <input
                  type="number"
                  defaultValue="1000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  AI Confidence minim pentru auto-fix (%)
                </label>
                <input
                  type="number"
                  defaultValue="95"
                  min="80"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Reguli Active</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {discrepancyData.checkRules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{rule.name}</div>
                      <div className="text-xs text-gray-500">{rule.description}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {rule.frequency} • {rule.successRate}% success
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowRuleModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Test All Rules
            </button>
            <button className="btn-primary">
              <Shield className="w-4 h-4 mr-2" />
              Salvează Configurația
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VerificareDiscrepante;